/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,

	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]'
	},

	// Development mode (lazy evaluation)
	get isDevelopment() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Logging system
const cartLog = (level, message, data = {}) => {
	if (!CART_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`🛒 [CART] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Debounce function
// Use global debounce from mm_utils.js
const cartDebounce = window.debounce || ((fn, delay) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => fn.apply(this, args), delay);
	};
});

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'cart'
	};

	return document.dispatchEvent(new CustomEvent(`cart:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const DOM = {
	get cart() { return document.querySelector(CART_CONFIG.selectors.cart); },
	get cartSection() { return document.getElementById(this.cart?.id || ''); },
	get cartProducts() { return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts); },
	get cartWrapper() { return document.querySelector(CART_CONFIG.selectors.cartWrapper); },
	get cartBubbles() { return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles); },

	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace('{{index}}', index)),

	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity)
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit('api-start', { method, path });

		const baseUrl = window.Shopify?.routes?.root || '/';
		const url = baseUrl + path;

		const isFormData = (typeof FormData !== 'undefined') && body instanceof FormData;

		const config = { method };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { 'Content-Type': 'application/json' };
		}

		if (body && method !== 'GET') {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || 'Shopify API Error');
			}

			cartEmit('api-success', { method, path, itemCount: data.item_count });
			// cart/add.js returns only line-item payload (no item_count).
			// Avoid writing an incorrect 0 into header/cart bubbles.
			if (typeof data.item_count !== 'undefined') {
				this.updateUI(data);
			}
			return data;
		} catch (error) {
			cartEmit('api-error', { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Updates cart UI elements with new data
	 */
	updateUI(data) {
		// Update cart count
		const count = data.item_count || 0;
		DOM.cartBubbles.forEach(bubble => {
			bubble.classList.toggle('mm-none', count === 0);
			bubble.textContent = count || '';
		});
		cartEmit('count-updated', { count });

		// Update cart content if sections exist
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement('div');
		temp.innerHTML = html;

		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);

		// Update cross-sell panel (lives outside [data-drawer-cart], needs separate update)
		const newCrossSellWrapper = temp.querySelector('[data-cart-cross-sell-wrapper]');
		const currentCrossSellWrapper = DOM.cartSection?.querySelector('[data-cart-cross-sell-wrapper]');
		if (newCrossSellWrapper && currentCrossSellWrapper) {
			currentCrossSellWrapper.innerHTML = newCrossSellWrapper.innerHTML;
		}

		// Le panneau cross-sell vit lui aussi hors [data-drawer-cart] (meme raison que ci-dessus)
		// — sans ce refresh, il garderait un etat "Ajoute" fige (check, bouton disabled, trash
		// visible) meme apres suppression du produit ailleurs dans le panier, puisque rien ne le
		// re-render ni ne reinitialise addedVariantIds.
		const newAbCrossSellWrapper = temp.querySelector('[data-ab-cross-sell-recommendation-wrapper]');
		const currentAbCrossSellWrapper = DOM.cartSection?.querySelector('[data-ab-cross-sell-recommendation-wrapper]');
		if (newAbCrossSellWrapper && currentAbCrossSellWrapper) {
			currentAbCrossSellWrapper.innerHTML = newAbCrossSellWrapper.innerHTML;
		}
		window.AbCrossSellPanel?._hydrateAlreadyAdded();

		if (newCart && currentCart) {
			currentCart.innerHTML = newCart.innerHTML;
			// Reinitialize Splide sliders with auto-init
			if (typeof Splide !== 'undefined') {
				document.querySelectorAll('.mm-drawer-cart .splide').forEach(function(element) {
					if (!element._splide) {
						try {
							new Splide(element).mount();
						} catch (error) {
							cartLog('warn', 'Failed to initialize Splide:', { element, error });
						}
					}
				});
			}
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}
			if (typeof window.initCartQuantityListeners === 'function') {
				window.initCartQuantityListeners();
			}
			// Reinitialize promo code listeners
			if (typeof initPromoCode === 'function') {
				initPromoCode();
			}
			cartEmit('refreshed');
		}
	}
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for cart drawer
	 */
	setupFocusTrap(cartWrapper, triggerElement) {
		const focusableElements = cartWrapper.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		// Sans déclencheur explicite (toaster, ajout au panier), on rend le focus à l'élément actif à l'ouverture
		this.focusTrap.triggerElement = triggerElement || document.activeElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes cart
			if (e.key === 'Escape' || e.key === 'Esc') {
				e.preventDefault();
				this.close();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);

		cartLog('info', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			cartLog('info', 'Focus trap removed');
		}
	},

	async get() {
		cartEmit('fetch-start');
		const data = await CartAPI.request('GET', 'cart.js');
		cartEmit('fetched', { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Open cart
	 */
	open(triggerElement) {
		if (!cartEmit('before-open', {}, true)) return;

		this.get().catch(error => cartLog('error', 'Failed to fetch cart on open', { error: error.message }));

		const wrapper = DOM.cartWrapper;

		if (wrapper) {
			wrapper.classList.add('mm-dc-is-open');
			window.lockScroll();
			document.documentElement.classList.add('mm-gorgias-hidden');

			// Hide overlays
			['axeptio_overlay'].forEach(id => {
				const elem = document.getElementById(id);
				if (elem) elem.style.display = 'none';
			});

			// Setup focus trap
			this.setupFocusTrap(wrapper, triggerElement);

			cartEmit('opened');
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit('before-close', {}, true)) return;

		const wrapper = DOM.cartWrapper;

		if (wrapper) wrapper.classList.remove('mm-dc-is-open');
		window.unlockScroll();
		document.documentElement.classList.remove('mm-gorgias-hidden');

		// Remove focus trap, puis focus rendu au déclencheur (lu avant : removeFocusTrap le remet à null).
		// Déclencheur masqué entre-temps (toaster refermé) : repli sur le bouton panier du header.
		const trigger = this.focusTrap.triggerElement;
		const focusWasInside = wrapper?.contains(document.activeElement) || document.activeElement === document.body;
		this.removeFocusTrap();
		if (focusWasInside) {
			const isReachable = trigger?.isConnected && trigger !== document.body && (!trigger.checkVisibility || trigger.checkVisibility({ visibilityProperty: true }));
			(isReachable ? trigger : document.querySelector('header [data-cart-toggle]'))?.focus();
		}

		// Show overlays
		['axeptio_overlay'].forEach(id => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = '';
		});

		cartEmit('closed');
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {}
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll('[data-property]');
		propertyElements.forEach(el => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll('[data-variant-option]');
		variantElements.forEach(el => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach(key => {
			if (!['cartItemId', 'lineIndex', 'maxQuantity'].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;

		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		elem.textContent = newQty;

		if (!skipEvent) {
			cartEmit('quantity-changed', { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	async updateQuantity(lineIndex, newQty) {
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = itemEl?.dataset.cartItemId;

		cartEmit('quantity-sync-start', { lineIndex, quantity: newQty, variantId });

		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

			// Shopify cart/change.js requires 1-based line index and proper sections format
			const requestBody = {
				line: parseInt(lineIndex, 10), // Should be 1-based from template
				quantity: newQty
			};

			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}

			const data = await CartAPI.request('POST', 'cart/change.js', requestBody);

			await this.processFreeProducts(data);
			cartEmit('quantity-synced', { lineIndex, quantity: newQty, variantId });
			return data;
		} catch (error) {
			cartEmit('quantity-sync-error', { lineIndex, error: error.message });
			// Fallback: refresh cart to sync with server state
			this.get().catch(error => cartLog('error', 'Failed to fetch cart on open', { error: error.message }));
			throw error;
		}
	},


	/**
	 * Process free products
	 * Reads data-free-products from the current DOM (server-rendered or refreshed via sections).
	 * Falls back to the live DOM when data.sections is unavailable (e.g. after cart.js GET).
	 */
	async processFreeProducts(data) {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		let freeProductsEl;

		if (data.sections?.[sectionId]) {
			const temp = document.createElement('div');
			temp.innerHTML = data.sections[sectionId];
			freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);
		} else {
			freeProductsEl = document.querySelector(CART_CONFIG.selectors.freeProducts);
		}

		if (!freeProductsEl?.children.length) return data;

		const updates = {};
		Array.from(freeProductsEl.children).forEach(el => {
			const variantId = el.dataset.variantId;
			const shouldHave = el.dataset.freeProduct === 'true';
			const hasIt = !!document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', variantId));

			if (hasIt && !shouldHave) {
				updates[variantId] = 0;
				cartEmit('free-product-removing', { variantId });
			} else if (!hasIt && shouldHave) {
				updates[variantId] = 1;
				cartEmit('free-product-adding', { variantId });
			}
		});

		if (Object.keys(updates).length > 0) {
			try {
				// Shopify cart/update.js expects updates object and optional sections
				const requestBody = { updates };

				if (sectionId) {
					requestBody.sections = sectionId;
				}

				await CartAPI.request('POST', 'cart/update.js', requestBody);
				cartEmit('free-products-updated', { updates });
			} catch (error) {
				cartEmit('free-products-error', { error: error.message });
			}
		}

		return data;
	}
};

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
const debouncedUpdate = cartDebounce((lineIndex) => {
	const quantity = Cart.getQuantity(lineIndex);
	Cart.updateQuantity(lineIndex, quantity);
}, CART_CONFIG.debounceDelay);

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: 'increase'
	};

	if (!cartEmit('before-quantity-increase', eventData, true)) return;

	if (newQty > max) {
		cartEmit('quantity-limit-reached', eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-increased', { ...eventData, newQty });
	debouncedUpdate(lineIndex);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: 'decrease'
	};

	if (!cartEmit('before-quantity-decrease', eventData, true)) return;

	if (newQty < 1) {
		// If quantity would be less than 1, remove the item completely
		const removeEventData = {
			...itemData,
			oldQty: current,
			action: 'remove'
		};

		if (!cartEmit('before-item-remove', removeEventData, true)) return;

		Cart.setQuantity(lineIndex, 0);
		cartEmit('item-removed', removeEventData);
		debouncedUpdate(lineIndex);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-decreased', eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: 'destroy'
	};

	if (!cartEmit('before-item-remove', eventData, true)) return;

	Cart.setQuantity(lineIndex, 0);
	cartEmit('item-removed', eventData);
	debouncedUpdate(lineIndex);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
window.mmOpenCart = (triggerElement) => Cart.open(triggerElement);
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);
window.mmReplaceCartAndOpen = async (items) => {
	const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
	await CartAPI.request('POST', 'cart/clear.js');
	const body = sectionId ? { items, sections: sectionId } : { items };
	const data = await CartAPI.request('POST', 'cart/add.js', body);
	if (sectionId && data.sections?.[sectionId]) {
		CartAPI.refreshDOM(data.sections[sectionId]);
	}
	Cart.open();
};

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });
window.CartAPI = CartAPI;

// Adds loading state to checkout button
window.mmLoadThis = (element) => {
	if (element) {
		element.classList.add('mm-loading');
		cartEmit('checkout-loading', { element });
	}
};

// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Close cart functionality
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-cart-close]')) {
			e.preventDefault();
			Cart.close();
		}

		// Checkout button loading
		if (e.target.closest('[data-checkout-btn]')) {
			window.mmLoadThis(e.target.closest('[data-checkout-btn]'));
		}

		// Intercepte le CTA principal du panier pour ouvrir le panneau cross-sell au lieu du
		// checkout direct — uniquement en mobile (le panneau est un bottom-sheet mobile-only,
		// cache en CSS sur desktop — sans ce garde, le clic serait intercepte sans rien afficher,
		// bloquant le checkout sur desktop).
		const mainCta = e.target.closest('[data-checkout-btn].mm-dc-cta');
		if (mainCta && window.isMobile()) {
			e.preventDefault();
			mainCta.classList.remove('mm-loading');
			window.AbCrossSellPanel?.open(mainCta);
		}

		// Gorgias chat button — force load if lazy, then open
		if (e.target.closest('[data-gorgias-open]')) {
			e.preventDefault();
			document.documentElement.classList.remove('mm-gorgias-hidden');

			if (window.GorgiasChat?.open) {
				window.GorgiasChat.open();
			} else {
				if (typeof window.loadGorgias === 'function') window.loadGorgias();
				const poll = setInterval(() => {
					if (window.GorgiasChat?.open) {
						clearInterval(poll);
						window.GorgiasChat.open();
					}
				}, 100);
				setTimeout(() => clearInterval(poll), 5000);
			}
		}

		// Discount removal
		const discountButton = e.target.closest('[data-discount-remove]');
		if (discountButton) {
			e.preventDefault();
			const discountTitle = discountButton.getAttribute('data-discount-title');
			if (discountTitle) {
				window.removeDiscount(discountTitle);
			}
		}
	});
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
window.initAddToCartForms = () => {
	document.querySelectorAll('[data-type="add-to-cart-form"]:not([data-cart-handler-attached])').forEach(form => {
		form.setAttribute('data-cart-handler-attached', 'true');
		form.addEventListener('submit', async (e) => {
			const formaction = e.submitter?.getAttribute('formaction') || '';
			if (formaction.includes('/checkout')) return;
			e.preventDefault();

			const submitButton = e.submitter || form.querySelector('[type="submit"]');
			const formData = new FormData(form);

			if (!formData.get('id')) {
				cartLog('error', 'No variant ID found in form', { form });
				return;
			}

			// Add loading state
			if (submitButton) {
				submitButton.classList.add('mm-loading');
				submitButton.disabled = true;
			}

			// Extract all form data including properties and options
			const extractedData = {
				variantId: formData.get('id'),
				quantity: parseInt(formData.get('quantity') || 1, 10),
				properties: {},
				options: {},
				formEntries: {}
			};

			// Collect all form entries
			for (const [key, value] of formData.entries()) {
				extractedData.formEntries[key] = value;

				// Extract properties (custom fields)
				if (key.startsWith('properties[')) {
					const propName = key.match(/properties\[(.+)\]/)?.[1];
					if (propName && value) {
						extractedData.properties[propName] = value;
					}
				}

				// Extract options (variant selections like size, color)
				if (key.startsWith('option')) {
					extractedData.options[key] = value;
				}
			}

			cartEmit('add-to-cart-start', extractedData);

			try {
				const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

				// Add sections for cart update if available
				if (sectionId) {
					formData.append('sections', sectionId);
				}

				const response = await CartAPI.request('POST', 'cart/add.js', formData);

				// Refresh cart drawer DOM using sections returned by cart/add.js
				if (response.sections && DOM.cart) {
					const sectionId = DOM.cart.id.replace('shopify-section-', '');
					if (response.sections[sectionId]) {
						CartAPI.refreshDOM(response.sections[sectionId]);
					}
				}

				// cart/add.js returns the added line item, not full cart totals.
				// Re-fetch cart to get a reliable item_count for header bubbles.
				const refreshedCart = await Cart.get();
				cartEmit('add-to-cart-success', {
					...extractedData,
					response: response,
					cart: refreshedCart
				});

				await Cart.processFreeProducts(refreshedCart);

			} catch (error) {
				cartEmit('add-to-cart-error', {
					...extractedData,
					error: error.message
				});
				cartLog('error', 'Add to cart failed', {
					error: error.message,
					...extractedData
				});
			} finally {
				// Remove loading state
				if (submitButton) {
					submitButton.classList.remove('mm-loading');
					submitButton.disabled = false;
				}
			}
		});
	});
};

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
window.initCartQuantityListeners = function initCartQuantityListeners() {
	// Cart decrease buttons
	document.querySelectorAll('[data-cart-decrease]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartDecrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDecreaseProductQuantity(lineIndex);
			if (priceContainer) window.mmLoadThis(priceContainer);
		});
	});

	// Cart increase buttons
	document.querySelectorAll('[data-cart-increase]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartIncrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmIncreaseProductQuantity(lineIndex);
			if (priceContainer) window.mmLoadThis(priceContainer);
		});
	});

	// Cart remove buttons (delete item)
	document.querySelectorAll('[data-cart-remove]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartRemove;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDestroyProductFromCart(lineIndex);
			if (priceContainer) {
				window.mmLoadThis(priceContainer);
			} else {
				// For free products, load the button itself
				window.mmLoadThis(btn);
			}
		});
	});
}

/*********************************************************************************************
 * 💰 PROMO CODE MANAGEMENT
 */
function initPromoCode() {
	const applyBtn = document.querySelector('[data-action="apply-discount"]');
	const discountInput = document.querySelector('input[data-name="discount"]');

	cartLog('info', 'Initializing promo code', { applyBtn: !!applyBtn, discountInput: !!discountInput });

	if (applyBtn && discountInput) {
		applyBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			cartLog('info', 'Apply discount button clicked');
			applyDiscount();
		});
		discountInput.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				cartLog('info', 'Enter key pressed in discount input');
				applyDiscount();
			}
		});
		// Hide error when user starts typing
		discountInput.addEventListener('input', () => {
			hidePromoError();
		});
		cartLog('info', 'Promo code listeners attached successfully');
	}
}

async function applyDiscount() {
	cartLog('info', 'applyDiscount function called');

	const input = document.querySelector('input[data-name="discount"]');
	const button = document.querySelector('[data-action="apply-discount"]');

	if (!input || !button) {
		cartLog('error', 'Input or button not found', { input: !!input, button: !!button });
		return;
	}

	const code = input.value.trim();
	cartLog('info', 'Discount code entered', { code });

	if (!code) {
		cartLog('warn', 'Empty discount code');
		return;
	}

	// Format carte cadeau Shopify : 16 caracteres alphanumeriques en 4 groupes de 4
	// (avec ou sans tirets). Ce champ ne traite que les codes promo — on intercepte
	// avant l'appel API pour rediriger vers le checkout plutot que d'afficher
	// une erreur generique "code invalide".
	const GIFT_CARD_PATTERN = /^[A-Z0-9]{4}-?[A-Z0-9]{4}-?[A-Z0-9]{4}-?[A-Z0-9]{4}$/i;
	if (GIFT_CARD_PATTERN.test(code)) {
		cartLog('info', 'Gift card format detected in promo input', { code });
		showPromoError(input.dataset.errorGiftCard || 'Veuillez renseigner votre carte cadeau à l\'étape suivante');
		input.value = '';
		return;
	}

	// Disable button and show loading state
	button.disabled = true;
	button.classList.add('mm-loading');

	try {
		// Get cart state before applying discount
		cartLog('info', 'Getting cart state before discount...');
		const cartBefore = await CartAPI.request('GET', 'cart.js');
		const discountsBefore = cartBefore.cart_level_discount_applications || [];
		cartLog('info', 'Cart before', {
			total: cartBefore.total_price,
			discounts: discountsBefore.length,
			discountsList: discountsBefore
		});

		// Check if this exact code is already applied
		const codeAlreadyApplied = discountsBefore.some(d => {
			const discountCode = d.title || d.code;
			return discountCode && discountCode.toLowerCase() === code.toLowerCase();
		});

		if (codeAlreadyApplied) {
			showPromoError(input.dataset.errorAlreadyApplied || 'Ce code est déjà appliqué');
			cartLog('info', 'This code is already applied', { code });
			input.value = ''; // Clear invalid code
			return;
		}


		// Check if cart is empty
		if (cartBefore.item_count === 0) {
			const errorMsg = input.dataset.errorEmptyCart || 'Votre panier est vide';
			showPromoError(errorMsg);
			cartLog('info', 'Cart is empty');
			input.value = ''; // Clear invalid code
			return;
		}

		// Apply discount via cart/update.js API
		cartLog('info', 'Applying discount via API...', { code });

		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		const updatePayload = {
			discount: code
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		cartLog('info', 'Cart updated', { updateData });

		// Verify if discount was applied — cart-level OR line-item (Buy X Get Y)
		const cartAfter = await CartAPI.request('GET', 'cart.js');

		// Shopify retient le code dans cart.discount_codes des qu'il est valide, meme
		// sans effet visible sur le panier (ex: livraison gratuite sans frais de port
		// selectionnes tant que le checkout n'est pas atteint). C'est la source la plus
		// fiable pour savoir si le code a ete accepte.
		const codeInDiscountCodes = cartAfter.discount_codes?.find(d =>
			d.code?.toLowerCase() === code.toLowerCase()
		);

		const codeAppliedCartLevel = cartAfter.cart_level_discount_applications?.some(d => {
			const discountTitle = d.title || d.code;
			return discountTitle && discountTitle.toLowerCase() === code.toLowerCase();
		});

		const lineItemDiscountAmount = codeAppliedCartLevel ? 0 : cartAfter.items?.reduce((sum, item) => {
			const match = item.line_level_discount_allocations?.find(a =>
				a.discount_application?.title?.toLowerCase() === code.toLowerCase()
			);
			return sum + (match ? match.amount : 0);
		}, 0);

		const codeAppliedLineItem = !codeAppliedCartLevel && lineItemDiscountAmount > 0;
		const codeApplied = codeInDiscountCodes
			? codeInDiscountCodes.applicable !== false
			: (codeAppliedCartLevel || codeAppliedLineItem);

		// Code retenu par Shopify mais sans effet visible sur cart-level/line-item
		// (ex: livraison gratuite avant selection des frais de port) : pas de rendu
		// Liquid disponible, on affiche le badge via le meme mecanisme que Buy X Get Y.
		const codeAppliedWithoutVisibleEffect = codeApplied && !codeAppliedCartLevel && !codeAppliedLineItem;

		if (!codeApplied) {
			// Discount was not applied - code is invalid
			cartLog('warn', 'Discount was not applied by Shopify', { code });

			// IMPORTANT: Force removal of invalid code from cart
			const cleanupPayload = {
				discount: '' // Empty string removes the discount
			};

			if (sectionId) {
				cleanupPayload.sections = sectionId;
			}

			try {
				await fetch(window.Shopify.routes.root + 'cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(cleanupPayload)
				});
				cartLog('info', 'Invalid code cleaned from cart', { code });
			} catch (cleanupError) {
				cartLog('error', 'Failed to clean invalid code', { error: cleanupError.message });
			}

			const errorMsg = input.dataset.errorNotApplicable || 'Ce code promo ne peut pas être appliqué à votre panier';
			showPromoError(errorMsg);
			input.value = ''; // Clear invalid code
			return;
		}

		// Discount applied successfully, refresh UI
		cartLog('info', 'Discount applied successfully', { cartLevel: codeAppliedCartLevel, lineItem: codeAppliedLineItem });

		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// For line-item discounts (Buy X Get Y), inject the code tag in the Applied Discounts zone
		// since Shopify doesn't populate cart_level_discount_applications for this type
		if (codeAppliedLineItem) {
			injectLineItemDiscountTag(code, lineItemDiscountAmount);
		} else if (codeAppliedWithoutVisibleEffect) {
			injectLineItemDiscountTag(code, 0);
		}

		// Clear input
		input.value = '';

		// Emit success event
		cartEmit('discount-applied', { code });
		cartLog('info', 'Discount applied and cart refreshed');

	} catch (error) {
		cartLog('error', 'Failed to apply discount', { error: error.message, stack: error.stack });
		showPromoError(input.dataset.errorText || 'Erreur lors de l\'application du code');
		input.value = ''; // Clear invalid code on error
	} finally {
		// Reset button state
		button.disabled = false;
		button.classList.remove('mm-loading');
	}
}

function showPromoError(message) {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.textContent = message;
		errorElement.classList.remove('mm-none');
	}

	if (input) {
		input.classList.add('error');
	}

	// Auto-hide after 5 seconds
	setTimeout(() => {
		hidePromoError();
	}, 5000);
}

function hidePromoError() {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.classList.add('mm-none');
		errorElement.textContent = '';
	}

	if (input) {
		input.classList.remove('error');
	}
}

// Tracks the active line-item discount code so it can be re-injected after DOM refreshes
let activeLineItemDiscountCode = null;
let isReinjectingLineItemDiscount = false;

function injectLineItemDiscountTag(code, amountCents) {
	const detailsEl = document.querySelector('.mm-dc-promo-details');
	if (!detailsEl) return;

	// Remove any existing injected tag for the same code (avoid duplicates after re-apply)
	const existing = detailsEl.querySelector(`[data-discount-code="${code.toLowerCase()}"][data-line-item-discount]`);
	if (existing) existing.remove();

	const formatted = amountCents > 0
		? `-${(amountCents / 100).toFixed(2).replace('.', ',')} €`
		: '';

	// Libellé traduisible : posé en data-attr sur l'input promo (fallback FR)
	const removeLabelTemplate = document.querySelector('input[data-name="discount"]')?.dataset.promoRemoveLabel || 'Supprimer le code promotionnel {{ code }}';
	const removeLabel = removeLabelTemplate.replace('{{ code }}', code);

	const html = `<div class="mm-dc-promo-applied mm-flex mm-align-center mm-justify-between" data-discount-code="${code.toLowerCase()}" data-line-item-discount>
		<button
			type="button"
			class="mm-dc-discount-button mm-cursor-pointer mm-flex mm-align-center overtitre"
			data-discount-remove
			data-discount-title="${code}"
			aria-label="${removeLabel}">
			${code}
			<svg class="mm-dc-discount-close" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:12px;height:12px;" aria-hidden="true">
				<path d="M3.95898 3.95801L16.0423 16.0413M16.0423 3.95801L3.95898 16.0413" stroke="var(--black-600)" stroke-width="1.5" stroke-linecap="square"/>
			</svg>
		</button>
		${formatted ? `<p class="mm-dc-discount-amount text-small mm-black mm-semibold">${formatted}</p>` : ''}
	</div>`;

	detailsEl.insertAdjacentHTML('beforeend', html);
	detailsEl.open = true;
	activeLineItemDiscountCode = code;
	cartLog('info', 'Line item discount tag injected', { code, amountCents });
}

window.removeDiscount = async function(code) {
	cartLog('info', 'Removing discount', { code });

	const discountButton = document.querySelector(`[data-discount-code="${code.toLowerCase()}"] [data-discount-remove]`);

	if (discountButton) {
		// Add loading state
		discountButton.disabled = true;
		discountButton.style.opacity = '0.5';
	}

	try {
		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Remove discount via cart/update.js API with empty discount parameter
		cartLog('info', 'Removing discount via API...', { code });

		const updatePayload = {
			discount: ''  // Empty string removes the discount
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		cartLog('info', 'Discount removal response', { updateData });

		// Verify discount was removed — check both cart-level and line-item discounts
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const discountStillPresentCartLevel = cartAfter.cart_level_discount_applications?.some(d => {
			const discountTitle = d.title || d.code;
			return discountTitle && discountTitle.toLowerCase() === code.toLowerCase();
		});
		const discountStillPresentLineItem = cartAfter.items?.some(item =>
			item.line_level_discount_allocations?.some(a =>
				a.discount_application?.title?.toLowerCase() === code.toLowerCase()
			)
		);

		if (discountStillPresentCartLevel || discountStillPresentLineItem) {
			cartLog('error', 'Discount was not removed', { code });
			throw new Error('Discount removal failed');
		}

		cartLog('info', 'Discount removed successfully', { code });

		// Clear tracked line-item discount if it matches
		if (activeLineItemDiscountCode?.toLowerCase() === code.toLowerCase()) {
			activeLineItemDiscountCode = null;
		}

		// Refresh UI
		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Emit success event
		cartEmit('discount-removed', { code });
		cartLog('info', 'Discount removed and cart refreshed');

	} catch (error) {
		cartLog('error', 'Failed to remove discount', { error: error.message, stack: error.stack });

		// Reset button state on error
		if (discountButton) {
			discountButton.disabled = false;
			discountButton.style.opacity = '1';
		}
	}
};

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
/**
 * Initial free products check on page load
 * Reads the server-rendered data-free-products and adds/removes free products if needed
 */
async function initFreeProducts() {
	const freeProductsEl = document.querySelector(CART_CONFIG.selectors.freeProducts);
	if (!freeProductsEl?.children.length) return;

	const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
	const updates = {};

	Array.from(freeProductsEl.children).forEach(el => {
		const variantId = el.dataset.variantId;
		const shouldHave = el.dataset.freeProduct === 'true';
		const hasIt = !!document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', variantId));

		if (hasIt && !shouldHave) {
			updates[variantId] = 0;
		} else if (!hasIt && shouldHave) {
			updates[variantId] = 1;
		}
	});

	if (Object.keys(updates).length > 0) {
		try {
			const requestBody = { updates };
			if (sectionId) requestBody.sections = sectionId;
			await CartAPI.request('POST', 'cart/update.js', requestBody);
		} catch (error) {
			cartLog('error', 'initFreeProducts failed', { error: error.message });
		}
	}
}

/**
 * Initial discount threshold check on page load
 * Reads the server-rendered data-discount-config and syncs discount state
 */
async function initDiscountThreshold() {
	const configEl = document.querySelector('[data-discount-config]');
	if (!configEl) return;

	const shouldBeActive = configEl.dataset.discountActive === 'true';
	const discountCode = configEl.dataset.discountCode;
	if (!discountCode) return;

	const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

	try {
		// Raw fetch — no updateUI/refreshDOM triggered
		const cartRaw = await fetch(window.Shopify.routes.root + 'cart.js');
		const cartData = await cartRaw.json();
		const isApplied = cartData.cart_level_discount_applications?.some(d => {
			const title = d.title || d.code || '';
			return title.toLowerCase() === discountCode.toLowerCase();
		});

		if (shouldBeActive && !isApplied) {
			const payload = { discount: discountCode };
			if (sectionId) payload.sections = sectionId;
			const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload)
			});
			const updated = await response.json();
			if (updated.sections?.[sectionId]) CartAPI.refreshDOM(updated.sections[sectionId]);
			cartEmit('auto-discount-applied', { code: discountCode });
		} else if (!shouldBeActive && isApplied) {
			const payload = { discount: '' };
			if (sectionId) payload.sections = sectionId;
			const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload)
			});
			const updated = await response.json();
			if (updated.sections?.[sectionId]) CartAPI.refreshDOM(updated.sections[sectionId]);
			cartEmit('auto-discount-removed', { code: discountCode });
		}
	} catch (error) {
		cartLog('error', 'initDiscountThreshold failed', { error: error.message });
	}
}

/*********************************************************************************************
 * 🔄 SUBSCRIBE BUTTON — Switch single purchase → subscription
 */
function initSubscribeBtn() {
	document.addEventListener('click', async function(e) {
		const btn = e.target.closest('[data-subscribe-btn]');
		if (!btn || btn.disabled) return;

		e.preventDefault();

		const variantId = parseInt(btn.dataset.variantId, 10);
		const sellingPlanId = parseInt(btn.dataset.sellingPlanId, 10);
		const lineIndex = parseInt(btn.dataset.lineIndex, 10);
		const sectionId = DOM.cart?.id?.replace('shopify-section-', '') || '';

		btn.disabled = true;
		btn.classList.add('mm-loading');

		try {
			// 1. Supprimer l'article achat unique (sans refresh DOM)
			await fetch(window.Shopify.routes.root + 'cart/change.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ line: lineIndex, quantity: 0 })
			});

			// 2. Ajouter la version abonnement avec sections pour le refresh DOM
			const addData = await CartAPI.request('POST', 'cart/add.js', {
				id: variantId,
				quantity: 1,
				selling_plan: sellingPlanId,
				sections: sectionId
			});

			// 3. cart/add.js ne retourne pas item_count → refreshDOM manuel
			if (addData.sections?.[sectionId]) {
				CartAPI.refreshDOM(addData.sections[sectionId]);
			}
		} catch (error) {
			cartLog('error', 'Subscribe button failed', { error: error.message });
			btn.disabled = false;
			btn.classList.remove('mm-loading');
		}
	});
}

/*********************************************************************************************
 * 🔄 UNSUBSCRIBE BUTTON — Switch subscription → single purchase
 */
function initUnsubscribeBtn() {
	document.addEventListener('click', async function(e) {
		const btn = e.target.closest('[data-unsubscribe-btn]');
		if (!btn || btn.disabled) return;

		e.preventDefault();

		const variantId = parseInt(btn.dataset.variantId, 10);
		const lineIndex = parseInt(btn.dataset.lineIndex, 10);
		const sectionId = DOM.cart?.id?.replace('shopify-section-', '') || '';

		btn.disabled = true;
		btn.classList.add('mm-loading');

		try {
			// 1. Supprimer l'article abonnement (sans refresh DOM)
			await fetch(window.Shopify.routes.root + 'cart/change.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ line: lineIndex, quantity: 0 })
			});

			// 2. Ajouter la version achat unique (pas de selling_plan) avec sections pour le refresh DOM
			const addData = await CartAPI.request('POST', 'cart/add.js', {
				id: variantId,
				quantity: 1,
				sections: sectionId
			});

			// 3. cart/add.js ne retourne pas item_count → refreshDOM manuel
			if (addData.sections?.[sectionId]) {
				CartAPI.refreshDOM(addData.sections[sectionId]);
			}
		} catch (error) {
			cartLog('error', 'Unsubscribe button failed', { error: error.message });
			btn.disabled = false;
			btn.classList.remove('mm-loading');
		}
	});
}

document.addEventListener('cart:refreshed', async () => {
	if (!activeLineItemDiscountCode) return;
	if (isReinjectingLineItemDiscount) return;

	isReinjectingLineItemDiscount = true;
	try {
		const cart = await CartAPI.request('GET', 'cart.js');
		const amount = cart.items?.reduce((sum, item) => {
			const match = item.line_level_discount_allocations?.find(a =>
				a.discount_application?.title?.toLowerCase() === activeLineItemDiscountCode.toLowerCase()
			);
			return sum + (match ? match.amount : 0);
		}, 0);

		// Toujours actif cote Shopify meme sans montant (ex: livraison gratuite)
		const stillApplicable = cart.discount_codes?.some(d =>
			d.code?.toLowerCase() === activeLineItemDiscountCode.toLowerCase() && d.applicable !== false
		);

		if (amount > 0 || stillApplicable) {
			injectLineItemDiscountTag(activeLineItemDiscountCode, amount);
		} else {
			activeLineItemDiscountCode = null;
		}
	} catch (error) {
		cartLog('error', 'Failed to re-inject line item discount tag', { error: error.message });
	} finally {
		isReinjectingLineItemDiscount = false;
	}
});

document.addEventListener('DOMContentLoaded', () => {
	cartEmit('initializing');
	initCartQuantityListeners();
	initPromoCode();
	initSubscribeBtn();
	initUnsubscribeBtn();

	// Retour arriere depuis /checkout specifiquement : le SSR Liquid de cette page a pu etre
	// genere par Shopify a partir d'un cookie panier pas encore synchronise (race condition
	// serveur cote Shopify — confirme : SSR voit 1 article, un cart.js quelques ms plus tard en
	// voit 2). On force donc l'ouverture du panier + un refresh des sections avec les vraies
	// donnees a jour, plutot qu'un refresh silencieux : ca garantit que l'utilisateur voit le bon
	// contenu meme si le panneau AB (B/C, cf plus bas) affichait un etat fige avant refresh.
	// Restreint a /checkout via document.referrer : `type === 'back_forward'` seul se declenche
	// sur N'IMPORTE QUEL retour arriere (depuis une page produit, collection, etc.), pas seulement
	// depuis checkout, ce qui ouvrait le panier partout sur le site.
	const isBackForwardNav = performance.getEntriesByType('navigation')[0]?.type === 'back_forward'
		&& /\/checkout([/?]|$)/.test(new URL(document.referrer || '', window.location.origin).pathname);

	Cart.get()
		.then(data => {
			cartEmit('initialized', { itemCount: data.item_count, totalPrice: data.total_price });
			initCartEventListeners();
			window.initAddToCartForms();
			initFreeProducts();
			initDiscountThreshold();

			if (isBackForwardNav) {
				const sectionId = DOM.cart?.id.replace('shopify-section-', '');
				if (sectionId) {
					// fetch direct (pas CartAPI.request) avec cache: 'no-store' — sur une
					// navigation back_forward, le navigateur peut reservir une reponse cart.js
					// mise en cache (sans sections) au lieu de refaire un vrai aller-retour reseau,
					// ce qui renvoyait `sections: {}` malgre le parametre demande dans l'URL.
					const baseUrl = window.Shopify?.routes?.root || '/';
					fetch(`${baseUrl}cart.js?sections=${sectionId}`, { cache: 'no-store' })
						.then((response) => response.json())
						.then((refreshData) => {
							if (refreshData.sections?.[sectionId]) {
								CartAPI.refreshDOM(refreshData.sections[sectionId]);
							}
						})
						.catch((error) => {
							cartLog('error', 'Failed to refresh cart on back/forward navigation', { error: error.message });
						})
						.finally(() => {
							Cart.open();
						});
				}
			}
		})
		.catch(error => {
			cartLog('error', 'Cart initialization failed', { error: error.message });
			cartEmit('initialization-error', { error: error.message });
		});
});

/*********************************************************************************************
 * 💰 CART SAVINGS BANNER
 */
const CartSavingsManager = {
	selectors: {
		banner: '#mm-dc-savings',
		title: '.mm-dc-savings-title',
	},

	init() {
		this._fetchAndUpdate();
		document.addEventListener('cart:refreshed', () => this._fetchAndUpdate());
		document.addEventListener('cart:opened', () => this._fetchAndUpdate());
	},

	async _fetchAndUpdate() {
		try {
			const data = await CartAPI.request('GET', 'cart.js');
			this._update(data.items);
		} catch (e) {
			cartLog('warn', 'CartSavings fetch failed', { error: e.message });
		}
	},

	_update(items) {
		const banner = document.querySelector(this.selectors.banner);
		if (!banner) return;

		if (!items) {
			this._fetchAndUpdate();
			return;
		}

		const freeProductIds = (banner.dataset.freeProductIds || '').split(',').filter(Boolean);
		let hasSubscription = false;

		let savings = 0;

		for (const item of items) {
			const isFree = freeProductIds.includes(String(item.id));
			if (isFree) continue;

			if (item.selling_plan_allocation) {
				hasSubscription = true;
				const spa = item.selling_plan_allocation;
				const compareAt = spa.compare_at_price > spa.price ? spa.compare_at_price : spa.price;
				savings += (compareAt - spa.price) * item.quantity;
			}
		}

		// Ajouter les économies des produits non-abonnement lues depuis le DOM
		document.querySelectorAll('.mm-dc-product-price-container[data-savings]').forEach(el => {
			const s = parseInt(el.dataset.savings, 10);
			if (s > 0) savings += s;
		});

		if (!hasSubscription) {
			banner.classList.add('mm-none');
			return;
		}
		banner.classList.toggle('mm-none', savings <= 0);

		if (savings > 0) {
			const titleEl = banner.querySelector(this.selectors.title);
			const template = banner.dataset.savingsTemplate;
			if (titleEl && template) {
				titleEl.textContent = template.replace('{{ montant }}', this._formatMoney(savings));
			}
		}
	},

	_formatMoney(cents) {
		if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
		return (cents / 100).toFixed(2).replace('.', ',') + ' €';
	},
};

CartSavingsManager.init();

/*********************************************************************************************
 * 🧪 PANNEAU CROSS-SELL (bottom-sheet)
 * Panneau intermediaire affiche au clic sur le CTA principal du panier (mobile uniquement) : un
 * produit cross-sell + un bloc recommandation, avant de continuer vers le checkout. Vit en
 * sibling de .mm-drawer-cart (voir mm-cart.liquid) pour survivre au refreshDOM des ajouts AJAX.
 */
const AbCrossSellPanelManager = {
	config: {
		wrapper: '[data-ab-cross-sell-recommendation-wrapper]',
		selectors: {
			panel: '[data-ab-cross-sell-panel]',
			close: '[data-ab-cross-sell-close]',
			cta: '[data-ab-cross-sell-cta]',
			ctaLabel: '[data-ab-cross-sell-cta-label]',
			item: '[data-ab-cross-sell-item]',
			addBtn: '[data-ab-cross-sell-add]',
			addLabel: '[data-ab-cross-sell-add-label]',
			priceSeparator: '[data-ab-cross-sell-price-separator]',
			price: '[data-ab-cross-sell-price]',
			check: '[data-ab-cross-sell-check]',
			removeBtn: '[data-ab-cross-sell-remove]',
		},
		openClass: 'mm-ab-cross-sell-is-open',
	},

	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null,
	},

	// Set of variant IDs (string) ajoutes depuis le panneau pendant cette session d'ouverture
	addedVariantIds: new Set(),

	init() {
		if (!document.querySelector(this.config.wrapper)) return;

		this._hydrateAlreadyAdded();
		this._bindEvents();
		this._bindBfcacheRestore();
		window.log?.('info', '🧪 [AB_CROSS_SELL_PANEL]', 'Initialized');
	},

	// Le CTA du panneau (data-checkout-btn + data-ab-cross-sell-cta) navigue vers /checkout sans
	// preventDefault (contrairement au CTA principal .mm-dc-cta) : il garde donc son etat
	// mm-loading et le panneau reste ouvert (body.mm-ab-cross-sell-is-open) au moment du depart.
	// Au retour arriere, si le navigateur restaure la page depuis le bfcache (JS fige, pas de
	// reload), ces deux etats restent tels quels — panneau visuellement ouvert + loader infini.
	// event.persisted === true signale precisement cette restauration bfcache.
	_bindBfcacheRestore() {
		window.addEventListener('pageshow', (e) => {
			if (!e.persisted) return;

			this.close();
			document.querySelectorAll('[data-checkout-btn].mm-loading').forEach((btn) => {
				btn.classList.remove('mm-loading');
			});
		});
	},

	// (Re)construit addedVariantIds a partir du DOM (rendu par le Liquid via already_in_cart) pour
	// que le wording du CTA principal reflete l'etat reel du panier. Vide le Set avant de le
	// repeupler — idempotent, appele aussi bien au chargement initial qu'apres chaque refreshDOM
	// (ajout/suppression depuis le panneau OU depuis le panier principal) pour eviter qu'un
	// variant retire ailleurs ne reste marque "ajoute" indefiniment dans le panneau.
	_hydrateAlreadyAdded() {
		this.addedVariantIds.clear();
		document.querySelectorAll('[data-ab-cross-sell-already-added]').forEach((item) => {
			const variantId = item.dataset.cartItemId;
			if (variantId) this.addedVariantIds.add(variantId);
		});
		this._updateCta();
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			if (e.target.closest(this.config.selectors.close)) {
				e.preventDefault();
				this.close();
				return;
			}

			const removeBtn = e.target.closest(this.config.selectors.removeBtn);
			if (removeBtn) {
				e.preventDefault();
				this._removeItem(removeBtn.closest(this.config.selectors.item));
			}
		});

		// Garde-fou : bloque tout re-submit d'un item deja ajoute (double-clic rapide avant que
		// `disabled` ne soit applique, touche Entree, etc.) — capture pour passer avant le
		// handler generique initAddToCartForms.
		document.addEventListener('submit', (e) => {
			const item = e.target.closest?.(this.config.selectors.item);
			if (!item) return;

			const variantId = item.dataset.cartItemId;
			if (variantId && this.addedVariantIds.has(variantId)) {
				e.preventDefault();
				e.stopImmediatePropagation();
			}
		}, true);

		document.addEventListener('cart:add-to-cart-success', (e) => {
			const variantId = e.detail?.data?.variantId;
			if (!variantId) return;

			const item = document.querySelector(this.config.wrapper)?.querySelector(
				`${this.config.selectors.item}[data-cart-item-id="${variantId}"]`
			);
			if (item) this._markAsAdded(item);
		});
	},

	open(triggerElement) {
		const wrapper = document.querySelector(this.config.wrapper);
		const panel = wrapper?.querySelector(this.config.selectors.panel);
		if (!wrapper || !panel) return;

		document.body.classList.add(this.config.openClass);
		window.lockScroll();
		this._setupFocusTrap(panel, triggerElement);

		cartEmit('ab-cross-sell-panel-opened');
	},

	close() {
		document.body.classList.remove(this.config.openClass);
		window.unlockScroll();
		this._removeFocusTrap();

		cartEmit('ab-cross-sell-panel-closed');
	},

	_markAsAdded(item) {
		const variantId = item.dataset.cartItemId;
		this.addedVariantIds.add(variantId);

		const addBtn = item.querySelector(this.config.selectors.addBtn);
		const addLabel = item.querySelector(this.config.selectors.addLabel);
		const priceSeparator = item.querySelector(this.config.selectors.priceSeparator);
		const price = item.querySelector(this.config.selectors.price);
		const check = item.querySelector(this.config.selectors.check);
		const removeBtn = item.querySelector(this.config.selectors.removeBtn);

		if (addBtn) addBtn.disabled = true;
		if (addLabel) addLabel.textContent = addLabel.dataset.addedLabel || addLabel.textContent;
		if (priceSeparator) priceSeparator.classList.add('mm-none');
		if (price) price.classList.add('mm-none');
		if (check) check.classList.remove('mm-none');
		if (removeBtn) removeBtn.classList.remove('mm-none');

		this._updateCta();
	},

	async _removeItem(item) {
		if (!item) return;
		const variantId = item.dataset.cartItemId;
		if (!variantId) return;

		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
			const requestBody = { updates: { [variantId]: 0 } };
			if (sectionId) requestBody.sections = sectionId;

			await CartAPI.request('POST', 'cart/update.js', requestBody);

			this.addedVariantIds.delete(variantId);

			const addBtn = item.querySelector(this.config.selectors.addBtn);
			const addLabel = item.querySelector(this.config.selectors.addLabel);
			const priceSeparator = item.querySelector(this.config.selectors.priceSeparator);
			const price = item.querySelector(this.config.selectors.price);
			const check = item.querySelector(this.config.selectors.check);
			const removeBtn = item.querySelector(this.config.selectors.removeBtn);

			if (addBtn) addBtn.disabled = false;
			if (addLabel) addLabel.textContent = addLabel.dataset.defaultLabel || addLabel.textContent;
			if (priceSeparator) priceSeparator.classList.remove('mm-none');
			if (price) price.classList.remove('mm-none');
			if (check) check.classList.add('mm-none');
			if (removeBtn) removeBtn.classList.add('mm-none');

			this._updateCta();
			cartEmit('ab-cross-sell-item-removed', { variantId });
		} catch (error) {
			cartLog('error', 'AB cross-sell item removal failed', { error: error.message, variantId });
		}
	},

	_updateCta() {
		const wrapper = document.querySelector(this.config.wrapper);
		const cta = wrapper?.querySelector(this.config.selectors.cta);
		const label = wrapper?.querySelector(this.config.selectors.ctaLabel);
		if (!cta || !label) return;

		const hasAdded = this.addedVariantIds.size > 0;
		const text = hasAdded ? cta.dataset.ctaAddedText : cta.dataset.ctaDefaultText;
		if (text) {
			label.textContent = text;
			cta.setAttribute('aria-label', text);
		}
	},

	_setupFocusTrap(panel, triggerElement) {
		const focusableElements = panel.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);
		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		this.focusTrap.handleKeyDown = (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				e.preventDefault();
				// Lu avant close() : _removeFocusTrap remet le déclencheur à null
				const trigger = this.focusTrap.triggerElement;
				this.close();
				trigger?.focus();
				return;
			}

			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else if (document.activeElement === this.focusTrap.lastFocusable) {
				e.preventDefault();
				this.focusTrap.firstFocusable?.focus();
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);
	},

	_removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
		}
	},
};

window.AbCrossSellPanel = AbCrossSellPanelManager;

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => AbCrossSellPanelManager.init());
} else {
	AbCrossSellPanelManager.init();
}

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (CART_CONFIG.isDevelopment) {
	['opened', 'closed', 'quantity-increased', 'quantity-decreased', 'free-product-adding', 'count-updated', 'add-to-cart-start', 'add-to-cart-success', 'add-to-cart-error'].forEach(event => {
		document.addEventListener(`cart:${event}`, e =>
			console.log(`🛒 [CART] ${event}:`, e.detail)
		);
	});
}
